import React, { useState, useMemo, useEffect } from "react";
import api from "../../api/api";
import Button from "../../components/ui/Button";
import SearchInput from "../../components/ui/SearchInput";
import Heading from "../../components/ui/Heading";
import { PlusCircle , FileEdit } from "lucide-react";

/* ---------------- ICONS ---------------- */
const GridIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
);

const ListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <line x1="8" y1="6" x2="21" y2="6"></line>
    <line x1="8" y1="12" x2="21" y2="12"></line>
    <line x1="8" y1="18" x2="21" y2="18"></line>
    <line x1="3" y1="6" x2="3.01" y2="6"></line>
    <line x1="3" y1="12" x2="3.01" y2="12"></line>
    <line x1="3" y1="18" x2="3.01" y2="18"></line>
  </svg>
);

/* ---------------- FORM ---------------- */
function BankAccountForm({ initialValues = {}, onSave, onCancel }) {
  const [form, setForm] = useState({
    holderName: "",
    bankName: "",
    accountNo: "",
    ifsc: "",
    branch: "",
    remarks: "",
    upiCode: "",       
    active: 1, 
    ...initialValues,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
      if (initialValues) {
    setForm({
      Bid: initialValues.Bid || 0,
      holderName: initialValues.holderName || "",
      bankName: initialValues.bankName || "",
      accountNo: initialValues.accountNo || "",
      ifsc: initialValues.ifsc || "",
      branch: initialValues.branch || "",
      remarks: initialValues.remarks || "",
      upiCode: initialValues.upiCode || "",
      active: initialValues.active ?? 1,
    });
  }
  }, [initialValues]);

  const validate = (values) => {
    const e = {};
    if (!values.holderName?.trim()) e.holderName = "Enter holder name";
    if (!values.bankName?.trim()) e.bankName = "Enter bank name";
    if (!values.accountNo?.trim()) e.accountNo = "Enter account number";
    if (!values.upiCode?.trim()) e.upiCode = "Enter UPI Code";

    const ifsc = values.ifsc?.trim().toUpperCase() || "";
    const ifscPattern = /^[A-Z0-9]{5,11}$/;
    if (!ifscPattern.test(ifsc)) {
      e.ifsc = "Invalid IFSC (e.g. ABCD0123456)";
    }
    if (!values.branch?.trim()) e.branch = "Enter branch";
    return e;
  };

  const handleChange = (field) => (e) => {
    let val = e.target.value;
    if (field === "ifsc") {
      val = val.toUpperCase(); // auto uppercase
    }
    setForm((prev) => ({ ...prev, [field]: val }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validate(form);
    setErrors(validation);
    if (Object.keys(validation).length) return;
    try {
      setSubmitting(true);
      await onSave(form);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto bg-white p-6 shadow rounded-xl">
      <h2 className="text-xl font-semibold mb-4 text-center">
        {initialValues?.Bid ? "Edit Bank Account" : "Add Bank Account"}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { label: "Holder Name", name: "holderName", placeholder: "John Doe" },
          { label: "Bank Name", name: "bankName", placeholder: "State Bank" },
          { label: "Account No", name: "accountNo", placeholder: "0123456789" },
          { label: "IFSC Code", name: "ifsc", placeholder: "ABCD0123456", maxLength: 11 },
          { label: "Branch", name: "branch", placeholder: "Branch Name" },
        ].map(({ label, name, ...rest }) => (
          <div key={name} className="sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <input
              {...rest}
              value={form[name] || ""}
              onChange={handleChange(name)}
              className={`mt-1 block w-full rounded-md border px-3 py-2 ${errors[name] ? "border-red-400" : "border-gray-300"}`}
            />
            {errors[name] && <p className="text-xs text-red-600 mt-1">{errors[name]}</p>}
          </div>
        ))}
<div className="sm:col-span-1 flex items-center gap-2 mt-3">
  <input
    type="checkbox"
    checked={form.active === 1}
    onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked ? 1 : 0 }))}
    className="h-4 w-4 text-blue-600 rounded border-gray-300"
  />
  <label className="text-sm font-medium text-gray-700">Active</label>
</div>

<div className="sm:col-span-2">
  <label className="block text-sm font-medium text-gray-700">UPI Code</label>
  <input
    type="text"
    value={form.upiCode}
    onChange={(e) => setForm(prev => ({ ...prev, upiCode: e.target.value }))}
    className={`mt-1 block w-full rounded-md border px-3 py-2 ${errors.upiCode ? "border-red-400" : "border-gray-300"}`}
    placeholder="UPI@oksbi"
  />
  {errors.upiCode && <p className="text-xs text-red-600 mt-1">{errors.upiCode}</p>}
</div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700">Remarks</label>
          <textarea
            rows={3}
            value={form.remarks}
            onChange={handleChange("remarks")}
            className="mt-1 block w-full rounded-md border px-3 py-2 border-gray-300"
          />
        </div>
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <button type="submit" disabled={submitting} className="px-3 py-1 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600">
          {submitting ? "Saving..." : "Save"}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600">
          Cancel
        </button>
      </div>
    </form>
  );
}


export default function BankAccountListPage({ setView }) {
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("card");
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const fetchData = async () => {
    try {
      const res = await api.BankDetail("admin");
      if (Array.isArray(res?.Table)) {
        const mapped = res.Table.map((row) => ({
          Bid: row.Bid || 0,
          holderName: row.HolderName || row.AccountName || "",
          bankName: row.BankName || "",
          accountNo: row.AccNo || "",
          ifsc: row.IFSCCode || "",
          branch: row.Branch || "",
          remarks: row.Remarks || "",
        }));
        setAccounts(mapped);
      } else {
        setAccounts([]);
      }
    } catch (err) {
      setAccounts([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = async (bid) => {
    try {
      const res = await api.EditBank(bid);
      const row = res?.Table?.[0];
      if (row) {
      setEditingAccount({
  Bid: row.Bid,
  holderName: row.HolderName || "",
  bankName: row.BankName || "",
  accountNo: row.AccNo || "",
  ifsc: row.IFSCCode || "",
  branch: row.Branch || "",
  remarks: row.Remarks || "",
  upiCode: row.Upicode || "", 
  active: row.Active ?? 1      
});
        setShowForm(true);
      }
    } catch (err) {
      alert("Failed to load account for editing.");
    }
  };

  const filteredAccounts = useMemo(() => {
    return accounts.filter(
      (a) =>
        a.holderName?.toLowerCase().includes(search.toLowerCase()) ||
        a.bankName?.toLowerCase().includes(search.toLowerCase()) ||
        a.ifsc?.toLowerCase().includes(search.toLowerCase())
    );
  }, [accounts, search]);

  return (
    <div className="flex flex-col h-full">
      {/* if showForm is true → ONLY show form page */}
      {showForm ? (
        <div className="p-6">
          <BankAccountForm
            initialValues={editingAccount || {}}
            onSave={async (values) => {
              try {
             const payload = {
                 Bid: values.Bid || 0,
                 SqlAction: values.Bid ? "UPDATE" : "INSERT",
                 AccNo: values.accountNo,
                 BankName: values.bankName,
                 Branch: values.branch,
                 IFSCCode: values.ifsc,
                 AccountName: values.holderName,
                 UPICode: values.upiCode || "",
                 Active: values.active,
                 Remarks: values.remarks || "-"
            };
                await api.SaveBankOrimtbl(payload);
                await fetchData();
                setShowForm(false);
                setEditingAccount(null);
              } catch (err) {
                alert("Failed to save account. Please try again.");
              }
            }}
            onCancel={() => {
              setShowForm(false);
              setEditingAccount(null);
            }}
          />
        </div>
      ) : (
        <>
          {/* Header Section */}
          <div className="px-3 py-3 bg-gray-100 border-b">
            <div className="mb-3">
              <Heading>Bank Account Management</Heading>
            </div>

            <div className="flex items-center gap-2 mb-3">
              {/* New Button */}
              <Button
                className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
                  rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
                onClick={() => {
                  setEditingAccount(null);
                  setShowForm(true);
                }}
              >
                <PlusCircle size={18} />
                <span className="font-medium">New</span>
              </Button>

              <div className="flex-1" />

              {/* Grid / Table Toggle */}
              <div className="flex items-center justify-center p-1 bg-gray-100 rounded-lg">
                <button
                  onClick={() => setViewMode("card")}
                  className={`p-2 rounded ${viewMode === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
                >
                  <GridIcon />
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  className={`p-2 rounded ${viewMode === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
                >
                  <ListIcon />
                </button>
              </div>
            </div>

                <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
            onClick={() => setView("list")}
          >
            ⬅
          </Button>
          
               {/* Search */}
                <SearchInput
                placeholder="Search accounts..."
                className="flex-1"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* List Content */}
          <div className="p-4 flex-1 overflow-auto">
            {viewMode === "table" ? (
              <div className="overflow-x-auto bg-white shadow rounded-lg">
                <table className="min-w-full text-gray-900  text-sm">
                  <thead className="bg-gray-100 uppercase">
                    <tr>
                      {["Holder", "Bank", "Acc No", "IFSC", "Branch", "Action"].map((h) => (
                        <th key={h} className="px-4 py-2 text-left font-semibold">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredAccounts.length ? (
                      filteredAccounts.map((acc, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2">{acc.holderName}</td>
                          <td className="px-4 py-2">{acc.bankName}</td>
                          <td className="px-4 py-2">{acc.accountNo}</td>
                          <td className="px-4 py-2">{acc.ifsc}</td>
                          <td className="px-4 py-2">{acc.branch}</td>
                          <td className="px-4 py-2">
                            <button
                              onClick={() => handleEdit(acc.Bid)}
                              className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
             bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
             hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
             transition-all duration-300 ease-in-out"
                            > <FileEdit className="w-5 h-5" />
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-6 text-gray-500">
                          No accounts found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAccounts.length ? (
                  filteredAccounts.map((acc, i) => (
                    <div
                      key={i}
                      className="p-5 bg-gradient-to-br from-white to-gray-50 shadow-lg rounded-xl 
                      flex flex-col justify-between transition-transform transform hover:scale-105
                       hover:shadow-2xl border border-gray-100"
                    >
                      <div className="space-y-2">
                        <div className="text-lg font-bold text-gray-800 truncate">{acc.holderName}</div>
                        <div className="text-sm text-gray-700 font-medium">{acc.bankName}</div>
                        <div className="text-sm text-gray-700 mt-1">
                          <span className="font-semibold text-gray-600">{acc.accountNo}</span> |{" "}
                          <span className="uppercase">{acc.ifsc}</span>
                        </div>
                        <div className="text-sm text-gray-700">{acc.branch}</div>
                      </div>
                      <button
                        onClick={() => handleEdit(acc.Bid)}
                         className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
                        bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
                        hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
                        transition-all duration-300 ease-in-out"
                      ><FileEdit className="w-5 h-5" />
                        Edit
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center text-gray-500 py-6">No accounts found.</div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}



// import React, { useState, useMemo, useEffect } from "react";
// import api from "../../api/api";
// import Button from "../../components/ui/Button";
// import SearchInput from "../../components/ui/SearchInput";
// import Heading from "../../components/ui/Heading";
// import { PlusCircle , FileEdit } from "lucide-react";

// /* ---------------- ICONS ---------------- */
// const GridIcon = () => (
//   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
//     <rect x="3" y="3" width="7" height="7"></rect>
//     <rect x="14" y="3" width="7" height="7"></rect>
//     <rect x="14" y="14" width="7" height="7"></rect>
//     <rect x="3" y="14" width="7" height="7"></rect>
//   </svg>
// );

// const ListIcon = () => (
//   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
//     <line x1="8" y1="6" x2="21" y2="6"></line>
//     <line x1="8" y1="12" x2="21" y2="12"></line>
//     <line x1="8" y1="18" x2="21" y2="18"></line>
//     <line x1="3" y1="6" x2="3.01" y2="6"></line>
//     <line x1="3" y1="12" x2="3.01" y2="12"></line>
//     <line x1="3" y1="18" x2="3.01" y2="18"></line>
//   </svg>
// );

// /* ---------------- FORM ---------------- */
// function BankAccountForm({ initialValues = {}, onSave, onCancel }) {
//   const [form, setForm] = useState({
//     holderName: "",
//     bankName: "",
//     accountNo: "",
//     ifsc: "",
//     branch: "",
//     remarks: "",
//     ...initialValues,
//   });
//   const [errors, setErrors] = useState({});
//   const [submitting, setSubmitting] = useState(false);

//   useEffect(() => {
//     setForm({
//       Bid: initialValues.Bid || 0,
//       holderName: initialValues.holderName || "",
//       bankName: initialValues.bankName || "",
//       accountNo: initialValues.accountNo || "",
//       ifsc: initialValues.ifsc || "",
//       branch: initialValues.branch || "",
//       remarks: initialValues.remarks || "",
//     });
//   }, [initialValues]);

//   const validate = (values) => {
//     const e = {};
//     if (!values.holderName?.trim()) e.holderName = "Enter holder name";
//     if (!values.bankName?.trim()) e.bankName = "Enter bank name";
//     if (!values.accountNo?.trim()) e.accountNo = "Enter account number";
//     const ifsc = values.ifsc?.trim().toUpperCase() || "";
//     const ifscPattern = /^[A-Z0-9]{5,11}$/;
//     if (!ifscPattern.test(ifsc)) {
//       e.ifsc = "Invalid IFSC (e.g. ABCD0123456)";
//     }
//     if (!values.branch?.trim()) e.branch = "Enter branch";
//     return e;
//   };

//   const handleChange = (field) => (e) => {
//     let val = e.target.value;
//     if (field === "ifsc") {
//       val = val.toUpperCase(); // auto uppercase
//     }
//     setForm((prev) => ({ ...prev, [field]: val }));
//     setErrors((prev) => ({ ...prev, [field]: undefined }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     const validation = validate(form);
//     setErrors(validation);
//     if (Object.keys(validation).length) return;
//     try {
//       setSubmitting(true);
//       await onSave(form);
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   return (
//     <form onSubmit={handleSubmit} className="max-w-2xl mx-auto bg-white p-6 shadow rounded-xl">
//       <h2 className="text-xl font-semibold mb-4 text-center">
//         {initialValues?.Bid ? "Edit Bank Account" : "Add Bank Account"}
//       </h2>
//       <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//         {[
//           { label: "Holder Name", name: "holderName", placeholder: "John Doe" },
//           { label: "Bank Name", name: "bankName", placeholder: "State Bank" },
//           { label: "Account No", name: "accountNo", placeholder: "0123456789" },
//           { label: "IFSC Code", name: "ifsc", placeholder: "ABCD0123456", maxLength: 11 },
//           { label: "Branch", name: "branch", placeholder: "Branch Name" },
//         ].map(({ label, name, ...rest }) => (
//           <div key={name} className="sm:col-span-1">
//             <label className="block text-sm font-medium text-gray-700">{label}</label>
//             <input
//               {...rest}
//               value={form[name] || ""}
//               onChange={handleChange(name)}
//               className={`mt-1 block w-full rounded-md border px-3 py-2 ${errors[name] ? "border-red-400" : "border-gray-300"}`}
//             />
//             {errors[name] && <p className="text-xs text-red-600 mt-1">{errors[name]}</p>}
//           </div>
//         ))}

//         <div className="sm:col-span-2">
//           <label className="block text-sm font-medium text-gray-700">Remarks</label>
//           <textarea
//             rows={3}
//             value={form.remarks}
//             onChange={handleChange("remarks")}
//             className="mt-1 block w-full rounded-md border px-3 py-2 border-gray-300"
//           />
//         </div>
//       </div>
//       <div className="mt-6 flex justify-end gap-3">
//         <button type="submit" disabled={submitting} className="px-3 py-1 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600">
//           {submitting ? "Saving..." : "Save"}
//         </button>
//         <button type="button" onClick={onCancel} className="px-3 py-1 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600">
//           Cancel
//         </button>
//       </div>
//     </form>
//   );
// }


// export default function BankAccountListPage({ setView }) {
//   const [accounts, setAccounts] = useState([]);
//   const [search, setSearch] = useState("");
//   const [viewMode, setViewMode] = useState("card");
//   const [showForm, setShowForm] = useState(false);
//   const [editingAccount, setEditingAccount] = useState(null);

//   const fetchData = async () => {
//     try {
//       const res = await api.BankDetail("admin");
//       if (Array.isArray(res?.Table)) {
//         const mapped = res.Table.map((row) => ({
//           Bid: row.Bid || 0,
//           holderName: row.HolderName || row.AccountName || "",
//           bankName: row.BankName || "",
//           accountNo: row.AccNo || "",
//           ifsc: row.IFSCCode || "",
//           branch: row.Branch || "",
//           remarks: row.Remarks || "",
//         }));
//         setAccounts(mapped);
//       } else {
//         setAccounts([]);
//       }
//     } catch (err) {
//       setAccounts([]);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//   }, []);

//   const handleEdit = async (bid) => {
//     try {
//       const res = await api.EditBank(bid);
//       const row = res?.Table?.[0];
//       if (row) {
//         setEditingAccount({
//           Bid: row.Bid,
//           holderName: row.HolderName || "",
//           bankName: row.BankName || "",
//           accountNo: row.AccNo || "",
//           ifsc: row.IFSCCode || "",
//           branch: row.Branch || "",
//           remarks: row.Remarks || "",
//         });
//         setShowForm(true);
//       }
//     } catch (err) {
//       alert("Failed to load account for editing.");
//     }
//   };

//   const filteredAccounts = useMemo(() => {
//     return accounts.filter(
//       (a) =>
//         a.holderName?.toLowerCase().includes(search.toLowerCase()) ||
//         a.bankName?.toLowerCase().includes(search.toLowerCase()) ||
//         a.ifsc?.toLowerCase().includes(search.toLowerCase())
//     );
//   }, [accounts, search]);

//   return (
//     <div className="flex flex-col h-full">
//       {/* if showForm is true → ONLY show form page */}
//       {showForm ? (
//         <div className="p-6">
//           <BankAccountForm
//             initialValues={editingAccount || {}}
//             onSave={async (values) => {
//               try {
//                 const payload = {
//                   Bid: values.Bid || 0,
//                   SqlAction: values.Bid ? "UPDATE" : "INSERT",
//                   BankName: values.bankName,
//                   Branch: values.branch,
//                   AccNo: values.accountNo,
//                   IFSCCode: values.ifsc,
//                   AccountName: values.holderName,
//                   Remarks: values.remarks || "",
//                 };
//                 await api.SaveBankOrimtbl(payload);
//                 await fetchData();
//                 setShowForm(false);
//                 setEditingAccount(null);
//               } catch (err) {
//                 alert("Failed to save account. Please try again.");
//               }
//             }}
//             onCancel={() => {
//               setShowForm(false);
//               setEditingAccount(null);
//             }}
//           />
//         </div>
//       ) : (
//         <>
//           {/* Header Section */}
//           <div className="px-3 py-3 bg-gray-100 border-b">
//             <div className="mb-3">
//               <Heading>Bank Account Management</Heading>
//             </div>

//             <div className="flex items-center gap-2 mb-3">
//               {/* New Button */}
//               <Button
//                 className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
//                   rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
//                 onClick={() => {
//                   setEditingAccount(null);
//                   setShowForm(true);
//                 }}
//               >
//                 <PlusCircle size={18} />
//                 <span className="font-medium">New</span>
//               </Button>

//               <div className="flex-1" />

//               {/* Grid / Table Toggle */}
//               <div className="flex items-center justify-center p-1 bg-gray-100 rounded-lg">
//                 <button
//                   onClick={() => setViewMode("card")}
//                   className={`p-2 rounded ${viewMode === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
//                 >
//                   <GridIcon />
//                 </button>
//                 <button
//                   onClick={() => setViewMode("table")}
//                   className={`p-2 rounded ${viewMode === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}
//                 >
//                   <ListIcon />
//                 </button>
//               </div>
//             </div>

//                 <div className="flex items-center gap-2">
//           <Button
//             variant="outline"
//             className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
//             onClick={() => setView("list")}
//           >
//             ⬅
//           </Button>
          
//             {/* Search */}
            
//               <SearchInput
//                 placeholder="Search accounts..."
//                 className="flex-1"
//                 value={search}
//                 onChange={(e) => setSearch(e.target.value)}
//               />
//             </div>
//           </div>

//           {/* List Content */}
//           <div className="p-4 flex-1 overflow-auto">
//             {viewMode === "table" ? (
//               <div className="overflow-x-auto bg-white shadow rounded-lg">
//                 <table className="min-w-full text-gray-900  text-sm">
//                   <thead className="bg-gray-100 uppercase">
//                     <tr>
//                       {["Holder", "Bank", "Acc No", "IFSC", "Branch", "Action"].map((h) => (
//                         <th key={h} className="px-4 py-2 text-left font-semibold">
//                           {h}
//                         </th>
//                       ))}
//                     </tr>
//                   </thead>
//                   <tbody className="divide-y">
//                     {filteredAccounts.length ? (
//                       filteredAccounts.map((acc, i) => (
//                         <tr key={i} className="hover:bg-gray-50">
//                           <td className="px-4 py-2">{acc.holderName}</td>
//                           <td className="px-4 py-2">{acc.bankName}</td>
//                           <td className="px-4 py-2">{acc.accountNo}</td>
//                           <td className="px-4 py-2">{acc.ifsc}</td>
//                           <td className="px-4 py-2">{acc.branch}</td>
//                           <td className="px-4 py-2">
//                             <button
//                               onClick={() => handleEdit(acc.Bid)}
//                               className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
//              bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
//              hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
//              transition-all duration-300 ease-in-out"
//                             > <FileEdit className="w-5 h-5" />
//                               Edit
//                             </button>
//                           </td>
//                         </tr>
//                       ))
//                     ) : (
//                       <tr>
//                         <td colSpan="6" className="text-center py-6 text-gray-500">
//                           No accounts found.
//                         </td>
//                       </tr>
//                     )}
//                   </tbody>
//                 </table>
//               </div>
//             ) : (
//               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
//                 {filteredAccounts.length ? (
//                   filteredAccounts.map((acc, i) => (
//                     <div
//                       key={i}
//                       className="p-5 bg-gradient-to-br from-white to-gray-50 shadow-lg rounded-xl 
//                       flex flex-col justify-between transition-transform transform hover:scale-105
//                        hover:shadow-2xl border border-gray-100"
//                     >
//                       <div className="space-y-2">
//                         <div className="text-lg font-bold text-gray-800 truncate">{acc.holderName}</div>
//                         <div className="text-sm text-gray-700 font-medium">{acc.bankName}</div>
//                         <div className="text-sm text-gray-700 mt-1">
//                           <span className="font-semibold text-gray-600">{acc.accountNo}</span> |{" "}
//                           <span className="uppercase">{acc.ifsc}</span>
//                         </div>
//                         <div className="text-sm text-gray-700">{acc.branch}</div>
//                       </div>
//                       <button
//                         onClick={() => handleEdit(acc.Bid)}
//                          className="self-start flex items-center justify-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white 
//              bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
//              hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
//              transition-all duration-300 ease-in-out"
//                       ><FileEdit className="w-5 h-5" />
//                         Edit
//                       </button>
//                     </div>
//                   ))
//                 ) : (
//                   <div className="col-span-full text-center text-gray-500 py-6">No accounts found.</div>
//                 )}
//               </div>
//             )}
//           </div>
//         </>
//       )}
//     </div>
//   );
// }
